/**
 * PIN Rate Limiting Hook
 * 
 * Tracks failed PIN attempts and implements rate limiting.
 * Locks authentication after 3 failures for 60 seconds.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const MAX_ATTEMPTS = 3;
const LOCK_DURATION_MS = 60000; // 60 seconds

interface PINRateLimitState {
  failedAttempts: number;
  isLocked: boolean;
  lockTimeRemaining: number;
  lockExpiresAt: number | null;
}

interface UsePINRateLimitReturn {
  failedAttempts: number;
  isLocked: boolean;
  lockTimeRemaining: number;
  recordFailure: () => void;
  resetAttempts: () => void;
}

/**
 * Hook to manage PIN rate limiting
 * 
 * Tracks failed attempts and locks authentication after MAX_ATTEMPTS failures.
 * Displays countdown timer during lock period.
 * Resets counter on successful authentication.
 */
export function usePINRateLimit(): UsePINRateLimitReturn {
  const [state, setState] = useState<PINRateLimitState>({
    failedAttempts: 0,
    isLocked: false,
    lockTimeRemaining: 0,
    lockExpiresAt: null,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Update countdown timer
  useEffect(() => {
    if (state.isLocked && state.lockExpiresAt) {
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Update countdown every second
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((state.lockExpiresAt! - now) / 1000));

        if (remaining <= 0) {
          // Lock expired, unlock
          setState(prev => ({
            ...prev,
            isLocked: false,
            lockTimeRemaining: 0,
            lockExpiresAt: null,
            failedAttempts: 0, // Reset attempts when lock expires
          }));
          
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        } else {
          // Update remaining time
          setState(prev => ({
            ...prev,
            lockTimeRemaining: remaining,
          }));
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state.isLocked, state.lockExpiresAt]);

  /**
   * Record a failed authentication attempt
   * Locks authentication after MAX_ATTEMPTS failures
   */
  const recordFailure = useCallback(() => {
    setState(prev => {
      const newAttempts = prev.failedAttempts + 1;

      if (newAttempts >= MAX_ATTEMPTS) {
        // Lock authentication
        const lockExpiresAt = Date.now() + LOCK_DURATION_MS;
        const lockTimeRemaining = Math.ceil(LOCK_DURATION_MS / 1000);

        return {
          failedAttempts: newAttempts,
          isLocked: true,
          lockTimeRemaining,
          lockExpiresAt,
        };
      }

      return {
        ...prev,
        failedAttempts: newAttempts,
      };
    });
  }, []);

  /**
   * Reset failed attempts counter
   * Called on successful authentication
   */
  const resetAttempts = useCallback(() => {
    setState({
      failedAttempts: 0,
      isLocked: false,
      lockTimeRemaining: 0,
      lockExpiresAt: null,
    });

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    failedAttempts: state.failedAttempts,
    isLocked: state.isLocked,
    lockTimeRemaining: state.lockTimeRemaining,
    recordFailure,
    resetAttempts,
  };
}
